/**
 * PATH: packages/ui/src/components/email/row/RowWorkChip.tsx
 * FILE_ID: UI-EMAIL-ROW-WORKCHIP-001
 * PURPOSE: The single "what work is this" chip for a row — a Board>Lane tag
 *          for an allocated/suggested row (with the lane handle right beside
 *          it AND — when the AI link is still pending — an always-visible
 *          ✓ confirm · ✗ reject riding the chip itself), or a linked-job pill
 *          (with its own inline ✓/✕) for a row that carries a job_id but no
 *          frame.
 * WHY:     Consolidates the board/job/suggested tag + lane into ONE fixed
 *          column of the row grammar so the workflow reads as a unit instead
 *          of being scattered across a scrolling chip strip.
 *          v5 (2026-06-22, operator: "confirm board should only be on hover on
 *          the arrow"): the framed AI review ✓/✗ moved OFF the bottom action
 *          bar (where the loud green "Confirm" competed with disposition) and
 *          ONTO the chip it refers to, right after the lane.
 *          v5.1 (2026-06-22, operator: "where did the tick and cross go… can't
 *          read the lanes"): v5.0 hid the ✓/✗ behind a chevron, adding a SECOND
 *          chevron next to the lane's own dropdown — the operator lost the
 *          actions and the lane got crowded. Reverted to ALWAYS-VISIBLE ✓/✗
 *          (no chevron, no collapse), honouring the hard-won lesson
 *          (BUG-2026-05-15 "where is the clear x and tick", REC narrow-crush
 *          "hover-only == invisible/untouchable"): an AI accept/reject is never
 *          hidden behind hover/tap.
 * FLOW:    accent + email + allocationChip → one Board>Lane (or job) chip
 *          (+ an always-visible review when the frame is pending).
 * RELATED:
 *   - row/resolveRowAccent.ts: supplies the frame label / icon / href / review.
 *   - row/RowActions.tsx: the bottom bar — now pure disposition (no review).
 *   - Test contract: data-testid="board-frame-tag" + the review/job aria-labels.
 */
import { Link } from 'react-router-dom'
import { Briefcase, Check, X } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { shortenEntityLabel } from '../entityLabel'
import type { EmailMessage } from '../types'
import type { RowAccent, RowReview } from './resolveRowAccent'
import type { AllocationFrame } from './rowTypes'

export interface RowWorkChipProps {
  accent: RowAccent
  allocationFrame?: AllocationFrame | null
  email: Pick<EmailMessage, 'job_id' | 'job_name' | 'job_auto_status'>
  allocationChip?: import('react').ReactNode
  jobPillClass?: string
  onConfirmJob?: () => void | Promise<void>
  isConfirmingJob?: boolean
  onRejectJob?: () => void | Promise<void>
  isRejectingJob?: boolean
  /** The framed-row AI review (board / suggested / framed-job). When it has a
   *  confirm or reject to offer, the chip shows an always-visible ✓/✗ right
   *  after the lane. Null/inert on unframed rows. */
  review?: RowReview
  /** Widen the Board>Lane chip at rest (skip the hover max-width expand).
   *  Used when the host forces the top inbox row fully open. */
  forceExpanded?: boolean
}

/** A round glyph button for the chip-borne review (✓ / ✗). Mirrors the
 *  linked-job pill's inline idiom so a framed and an unframed AI link read the
 *  same; h-5 (20px) for a friendlier tap target than the 16px pill buttons. */
const CHIP_REVIEW_BTN =
  'inline-flex items-center justify-center h-5 w-5 rounded-full transition-colors shrink-0 cursor-pointer touch-manipulation'

/**
 * ChipReview(review) → the ALWAYS-VISIBLE ✓ confirm · ✗ reject riding the work
 * chip it refers to (right after the lane), wrapped in ONE subtle capsule so
 * they read as the chip's review controls — not loose glyphs floating in the
 * chip lane.
 *
 * v5.2 (2026-06-22, operator screenshot "fix this … the expansion tray"): a
 * confirmed/linked frame shows only the ✗ (no ✓), and bare-red + floating in
 * the gap between the pinned chips and the scrolling tail it read as an ERROR /
 * broken element rather than an "unlink" control. Fix: contain ✓/✗ in a faint
 * capsule attached to the chip, and calm the lone reject to muted ink (→ red on
 * hover) so it reads as a quiet remove, not an alarm. The ✓ stays emerald
 * (the inviting, encouraged action).
 *
 * Discoverability contract (BUG-2026-05-15 "where is the clear x and tick";
 * REC narrow-crush "hover-only == invisible/untouchable"; operator 2026-06-22
 * v5.0 "where did the tick and cross go" when they were hidden behind a
 * chevron): NEVER hide an AI accept/reject behind hover/tap. The ✓/✗ stay
 * PAINTED at rest (no opacity-0, no group-hover gate, no chevron) — calming the
 * resting COLOUR of the reject is not hiding it; the X glyph is fully visible.
 */
function ChipReview({ review }: { review: RowReview }) {
  const { showConfirm, showReject, confirmLabel, rejectLabel, busy, onConfirm, onReject } = review

  return (
    <span className="inline-flex items-center gap-0.5 shrink-0 rounded-full border border-border/50 bg-muted/50 dark:bg-zinc-800/60 px-0.5 py-px">
      {showConfirm && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (busy) return
            void onConfirm?.()
          }}
          disabled={busy}
          aria-label={confirmLabel}
          title={confirmLabel}
          className={cn(
            CHIP_REVIEW_BTN,
            'text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-300',
            busy && 'cursor-wait opacity-70',
          )}
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
      {showReject && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (busy) return
            void onReject?.()
          }}
          disabled={busy}
          aria-label={rejectLabel}
          title={`${rejectLabel} (trains the matcher)`}
          className={cn(
            CHIP_REVIEW_BTN,
            // Calm at rest (a quiet "remove"), red on hover — never alarming a
            // confirmed allocation, never hidden (v5.2).
            'text-muted-foreground hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-300',
            busy && 'cursor-wait opacity-70',
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  )
}

/**
 * RowWorkChip(accent, email, …) → the Board>Lane tag, or the linked-job pill.
 * Renders null when the row is neither allocated nor linked to a job.
 */
export function RowWorkChip({
  accent,
  allocationFrame,
  email,
  allocationChip,
  jobPillClass,
  onConfirmJob,
  isConfirmingJob = false,
  onRejectJob,
  isRejectingJob = false,
  review,
  forceExpanded = false,
}: RowWorkChipProps) {
  const { isAllocated, frameIsJob, accentColor, FrameIcon, frameLabel, frameLabelFull, href } =
    accent

  if (isAllocated) {
    const tagInner = frameIsJob && email.job_id ? (
      <Link
        to={`/jobs/${email.job_id}`}
        data-testid="board-frame-tag"
        onClick={(e) => e.stopPropagation()}
        className="min-w-0 text-[10px] font-semibold tracking-wide truncate hover:underline"
        style={{ color: accentColor }}
      >
        {frameLabel}
      </Link>
    ) : href ? (
      <Link
        to={href}
        data-testid="board-frame-tag"
        onClick={(e) => e.stopPropagation()}
        title={`Open board · ${allocationFrame?.title || frameLabelFull}`}
        className="min-w-0 text-[10px] font-semibold tracking-wide truncate hover:underline cursor-pointer"
        style={{ color: accentColor }}
      >
        {frameLabel}
      </Link>
    ) : (
      <span
        data-testid="board-frame-tag"
        className="min-w-0 text-[10px] font-semibold tracking-wide truncate"
        style={{ color: accentColor }}
      >
        {frameLabel}
      </span>
    )

    return (
      // Wrap the board tag + lane + review in ONE tight unit (gap-1) so the
      // workflow reads as a single cluster and the review ✓/✗ stay visually
      // attached to the chip they refer to — rather than three loose chips
      // spaced gap-1.5 with the reject ✗ stranded in the seam between the
      // pinned lead and the scrolling tail (operator 2026-06-22 screenshot).
      // min-w-0 lets the board tag inside still truncate under width pressure.
      <span className="inline-flex items-center gap-1 min-w-0">
        {/* min-w-0 + max-w cap (NOT shrink-0): the tag truncates under width
            pressure instead of overflowing the chip lane and overlapping the
            row's trailing content (BUG-2026-06-21-EMAIL-ROW-CHIP-SPILL). */}
        <span
          // Hover-expand (operator 2026-06-23: "hovering over say error… should
          // auto expand it so I can read it fully"): the tag is capped at rest
          // so the strip stays compact, but the WHOLE row is a `group`, so on
          // row hover the cap lifts and the truncated board name (e.g. "Erro…")
          // animates out to its full width. It still truncates under genuine
          // width pressure rather than overflowing the lane
          // (BUG-2026-06-21-EMAIL-ROW-CHIP-SPILL). forceExpanded (inbox top
          // row) keeps the lifted cap at rest so the first card reads fully.
          className={cn(
            'inline-flex items-center gap-1 rounded-full border bg-card/80 pl-1.5 pr-2 py-0.5 min-w-0 transition-[max-width] duration-200 ease-out',
            forceExpanded
              ? 'max-w-[460px]'
              : 'max-w-[200px] group-hover:max-w-[460px]',
          )}
          style={{ borderColor: `color-mix(in srgb, ${accentColor} 55%, transparent)` }}
          title={allocationFrame?.title || frameLabelFull}
        >
          <FrameIcon className="h-3 w-3 shrink-0" style={{ color: accentColor }} aria-hidden />
          {tagInner}
        </span>
        {/* Board → Lane separator + lane handle. The explicit "›" makes the lane
            read as a DISTINCT segment of the workflow (operator 2026-06-23: "on
            expansion show the lane … I still don't see the lane I just see the
            board") rather than two same-looking pills the eye can't tell apart.
            Rendered only when there's a lane chip; null on job/suggested frames. */}
        {allocationChip && (
          <span
            aria-hidden
            className="shrink-0 text-muted-foreground/60 text-xs leading-none px-px"
          >
            ›
          </span>
        )}
        {allocationChip}
        {/* Pending AI review rides the chip (v5.1): the ✓/✗ are ALWAYS visible
            right after the lane — operator 2026-06-22 lost them when v5.0 hid
            them behind a chevron ("where did the tick and cross go"). v5.2
            contains them in a faint capsule so a lone reject reads as a quiet
            "remove" control, not a floating error. */}
        {review && (review.showConfirm || review.showReject) && (
          <ChipReview review={review} />
        )}
      </span>
    )
  }

  // Unframed row carrying a linked job → the classic job pill with its own
  // inline ✓ (confirm AI link) / ✕ (reject + train the matcher).
  if (!email.job_id) return null
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-medium max-w-[180px]',
        jobPillClass || 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      )}
    >
      <Link
        to={`/jobs/${email.job_id}`}
        onClick={(e) => e.stopPropagation()}
        title={email.job_name || `Job #${email.job_id}`}
        className="inline-flex items-center gap-1 hover:underline min-w-0"
      >
        <Briefcase className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">
          {shortenEntityLabel(email.job_name) || `#${email.job_id}`}
        </span>
      </Link>
      {onConfirmJob && email.job_auto_status === 'pending' && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isConfirmingJob) return
            void onConfirmJob()
          }}
          disabled={isConfirmingJob}
          title="Confirm this AI job allocation"
          aria-label="Confirm this AI job allocation"
          className={cn(
            'ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full transition-colors shrink-0',
            'opacity-70 hover:opacity-100 hover:bg-green-500/20 hover:text-green-700 dark:hover:text-green-300',
            isConfirmingJob && 'opacity-100 cursor-wait',
          )}
        >
          <Check className="h-3 w-3" />
        </button>
      )}
      {onRejectJob && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isRejectingJob) return
            void onRejectJob()
          }}
          disabled={isRejectingJob}
          title="Reject this job allocation (trains the matcher)"
          aria-label="Reject this job allocation"
          className={cn(
            'ml-1 -mr-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full transition-colors shrink-0',
            'opacity-70 hover:opacity-100 hover:bg-red-500/20 hover:text-red-700 dark:hover:text-red-300',
            isRejectingJob && 'opacity-100 cursor-wait',
          )}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}
